// ====================================================
// VidyaTrack — App Router  (v4 — dedicated pages)
// ====================================================

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { DataProvider } from "./contexts/DataContext";
import AppLayout from "./components/layout/AppLayout";

// ── Public ──────────────────────────────────────────
import LandingPage    from "./landing/LandingPage";
import LegalPage     from "./pages/legal/LegalPage";
import LoginPage      from "./components/auth/LoginPage";
import ForgotPassword from "./components/auth/ForgotPassword";

// ── Dashboards ──────────────────────────────────────
import AdminDashboard   from "./pages/dashboards/AdminDashboard";
import TeacherDashboard from "./pages/dashboards/TeacherDashboard";
import StudentDashboard from "./pages/dashboards/StudentDashboard";
import ParentDashboard  from "./pages/dashboards/ParentDashboard";

// ── Shared / unchanged ──────────────────────────────
import CalendarPage from "./pages/CalendarPage";
import ProfilePage  from "./pages/ProfilePage";
import ReportsPage  from "./pages/ReportsPage";

// ── STUDENT dedicated pages ─────────────────────────
import StudentHomework   from "./pages/student/StudentHomework";
import StudentAttendance from "./pages/student/StudentAttendance";
import StudentMaterials  from "./pages/student/StudentMaterials";
import StudentNotices    from "./pages/student/StudentNotices";
import StudentGroups     from "./pages/student/StudentGroups";

// ── TEACHER dedicated pages ─────────────────────────
import TeacherHomework   from "./pages/teacher/TeacherHomework";
import TeacherAttendance from "./pages/teacher/TeacherAttendance";
import TeacherMaterials  from "./pages/teacher/TeacherMaterials";
import TeacherGroups     from "./pages/teacher/TeacherGroups";
// Teacher reuses student notices + shared pages
import NoticesPage from "./pages/NoticesPage";

// ── PARENT dedicated pages ──────────────────────────
import ParentHomework   from "./pages/parent/ParentHomework";
import ParentAttendance from "./pages/parent/ParentAttendance";

// ── ADMIN dedicated pages ───────────────────────────
import StudentsPage from "./pages/admin/StudentsPage";
import ParentsPage  from "./pages/admin/ParentsPage";
import TeachersPage from "./pages/admin/TeachersPage";
import ClassesPage  from "./pages/admin/ClassesPage";

// ── Guards ─────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#f4f7fd" }}>
      <div style={{ textAlign:"center" }}>
        <div className="spinner" style={{ margin:"0 auto 12px" }}/>
        <p style={{ color:"var(--gray-500)",fontSize:".88rem" }}>Loading VidyaTrack…</p>
      </div>
    </div>
  );
  if (!currentUser) return <Navigate to="/login" state={{ from:location }} replace/>;
  return children;
}

function RoleGuard({ allowed, children }) {
  const { userData } = useAuth();
  if (!allowed.includes(userData?.role)) return <Navigate to="/dashboard" replace/>;
  return children;
}

function DashboardRouter() {
  const { userData } = useAuth();
  if (userData?.role === "admin")   return <AdminDashboard/>;
  if (userData?.role === "teacher") return <TeacherDashboard/>;
  if (userData?.role === "parent")  return <ParentDashboard/>;
  return <StudentDashboard/>;
}

// ── Protected app shell ────────────────────────────
function AppRoutes() {
  return (
    <ProtectedRoute>
      <AppLayout>
        <Routes>
          {/* Dashboard (role-auto) */}
          <Route path="/dashboard" element={<DashboardRouter/>}/>

          {/* ── STUDENT routes ── */}
          <Route path="/student/homework"   element={<RoleGuard allowed={["student"]}><StudentHomework/></RoleGuard>}/>
          <Route path="/student/attendance" element={<RoleGuard allowed={["student"]}><StudentAttendance/></RoleGuard>}/>
          <Route path="/student/materials"  element={<RoleGuard allowed={["student"]}><StudentMaterials/></RoleGuard>}/>
          <Route path="/student/notices"    element={<RoleGuard allowed={["student"]}><StudentNotices/></RoleGuard>}/>
          <Route path="/student/groups"     element={<RoleGuard allowed={["student"]}><StudentGroups/></RoleGuard>}/>
          <Route path="/student/calendar"   element={<RoleGuard allowed={["student"]}><CalendarPage/></RoleGuard>}/>

          {/* ── TEACHER routes ── */}
          <Route path="/teacher/homework"   element={<RoleGuard allowed={["teacher"]}><TeacherHomework/></RoleGuard>}/>
          <Route path="/teacher/attendance" element={<RoleGuard allowed={["teacher"]}><TeacherAttendance/></RoleGuard>}/>
          <Route path="/teacher/materials"  element={<RoleGuard allowed={["teacher"]}><TeacherMaterials/></RoleGuard>}/>
          <Route path="/teacher/groups"     element={<RoleGuard allowed={["teacher"]}><TeacherGroups/></RoleGuard>}/>
          <Route path="/teacher/notices"    element={<RoleGuard allowed={["teacher"]}><NoticesPage/></RoleGuard>}/>
          <Route path="/teacher/calendar"   element={<RoleGuard allowed={["teacher"]}><CalendarPage/></RoleGuard>}/>
          <Route path="/teacher/reports"    element={<RoleGuard allowed={["teacher"]}><ReportsPage/></RoleGuard>}/>

          {/* ── PARENT routes ── */}
          <Route path="/parent/homework"    element={<RoleGuard allowed={["parent"]}><ParentHomework/></RoleGuard>}/>
          <Route path="/parent/attendance"  element={<RoleGuard allowed={["parent"]}><ParentAttendance/></RoleGuard>}/>
          <Route path="/parent/notices"     element={<RoleGuard allowed={["parent"]}><StudentNotices/></RoleGuard>}/>
          <Route path="/parent/calendar"    element={<RoleGuard allowed={["parent"]}><CalendarPage/></RoleGuard>}/>

          {/* ── ADMIN routes ── */}
          <Route path="/admin/students"    element={<RoleGuard allowed={["admin"]}><StudentsPage/></RoleGuard>}/>
          <Route path="/admin/teachers"    element={<RoleGuard allowed={["admin"]}><TeachersPage/></RoleGuard>}/>
          <Route path="/admin/parents"     element={<RoleGuard allowed={["admin"]}><ParentsPage/></RoleGuard>}/>
          <Route path="/admin/classes"     element={<RoleGuard allowed={["admin"]}><ClassesPage/></RoleGuard>}/>
          <Route path="/admin/homework"    element={<RoleGuard allowed={["admin"]}><TeacherHomework/></RoleGuard>}/>
          <Route path="/admin/attendance"  element={<RoleGuard allowed={["admin"]}><TeacherAttendance/></RoleGuard>}/>
          <Route path="/admin/notices"     element={<RoleGuard allowed={["admin"]}><NoticesPage/></RoleGuard>}/>
          <Route path="/admin/calendar"    element={<RoleGuard allowed={["admin"]}><CalendarPage/></RoleGuard>}/>
          <Route path="/admin/reports"     element={<RoleGuard allowed={["admin"]}><ReportsPage/></RoleGuard>}/>

          {/* ── Shared ── */}
          <Route path="/profile" element={<ProfilePage/>}/>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </AppLayout>
    </ProtectedRoute>
  );
}

function AppContent() {
  return (
    <Routes>
      <Route path="/"               element={<LandingPage/>}/>
      <Route path="/login"          element={<LoginPage/>}/>
      <Route path="/forgot-password" element={<ForgotPassword/>}/>
      <Route path="/legal/:page"    element={<LegalPage/>}/>
      <Route path="/*"              element={<AppRoutes/>}/>
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <ThemeProvider>
      <AuthProvider>
        <DataProvider>
          <AppContent/>
          <ToastContainer
            position="top-right"
            autoClose={3500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnHover
            draggable
            theme="light"
          />
        </DataProvider>
      </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}
